import { FormattedMessage } from "@/i18n/i18n-client";
import styled from "styled-components";
import { DefaultTextPrice } from "@/components/Price";
import { FlexCol, FlexRow, H3 } from "@/shared/globals";
import { PrimaryButton } from "@/components/Button";
import { themeColor } from "@/shared/styles-utils";
import { useCartData } from "@/lib/cartData/useCartData";
import { isCartDirty } from "@/shared/utils/error-utils";
import { CartError, AuthMode } from "@/generated/graphql";
import { Link } from "@/i18n/i18n-navigation";
import { useStore } from "@/lib/storeData/StoreContext";
import { getPaymentLink } from "@/shared/utils/shinglePageCheckoutFunctions";
import { useAuth } from "@/lib/Authentication/hooks/useAuth";
import { useLocale } from "@/i18n/i18n-client";
import { useState } from "react";
import { useRouter } from "next/navigation";
import { CookieEnum, getCookie } from "@/shared/utils/cookie";
import CentralizedModal from "@/components/Centralizedlogin/CentralizedModal";
export interface SideCartFooterProps {
  updateCartItemsLoading?: boolean;
  onCheckoutClick?: () => void;
  isCheckingSession?: boolean;
}

const SideCartFooter: React.FC<SideCartFooterProps> = ({
  updateCartItemsLoading,
  onCheckoutClick,
  isCheckingSession,
}) => {
  const {
    cart: { id: cartId, isLoading, receipt, items, cartErrors },
    setSideCartState,
  } = useCartData();
  const { handle, WuiltPayState, authMode } = useStore();
  const canNotProceedToCheckout = isCartDirty(cartErrors as CartError[]);
  const { user, isLoggedIn } = useAuth();
  const token = user?.token;
  const { code: localeCode } = useLocale();
  const router = useRouter();
  const [isCentralizedModalOpen, setIsCentralizedModalOpen] = useState(false);

  const itemsCount = items?.reduce((acc, curr) => acc + curr.quantity, 0);

  const handleCheckoutClick = () => {
    // Check if authMode is Modern and user is not logged in and no central token exists
    if (authMode === AuthMode.Modern && !isLoggedIn) {
      const central_token = getCookie(CookieEnum.CLIENT_CENTRAL_TOKEN);
      if (!central_token) {
        setIsCentralizedModalOpen(true);
        return;
      }
    }

    // Close side cart and call the original callback
    setSideCartState(false);
    onCheckoutClick?.();

    // Proceed with normal checkout flow
    const checkoutUrl = WuiltPayState?.enabled
      ? getPaymentLink(handle, cartId, token, localeCode)
      : `/checkout/${cartId}`;

    router.push(checkoutUrl);
  };

  return (
    <Container fullWidth spacing="l">
      <Subtotal fullWidth justifyContent="space-between">
        <FlexRow spacing="xs">
          <H3>
            <FormattedMessage defaultMessage="Subtotal" id="L8seEc" />
          </H3>
          <H3 fontWeight={400}>
            <FormattedMessage
              defaultMessage="({itemsCount, plural, =0 {0 item} one {1 item} two {# items} few {# items} many {# items} other {# items}})"
              id="+B0hGt"
              values={{ itemsCount }}
            />
          </H3>
        </FlexRow>
        <H3 fontWeight={700} data-test="text-sidecart-subtotal">
          {updateCartItemsLoading ? (
            <FormattedMessage defaultMessage="Calculating..." id="EXlQBN" />
          ) : (
            <DefaultTextPrice money={receipt?.total} />
          )}
        </H3>
      </Subtotal>

      <Footer fullWidth justifyContent="space-between">
        <CheckoutButton
          data-test="button-checkout"
          fullWidth
          onClick={handleCheckoutClick}
          disabled={isLoading || canNotProceedToCheckout}
        >
          <FormattedMessage defaultMessage="Checkout" id="BJ2TKX" />
        </CheckoutButton>
        <Link fullWidth href="/cart">
          <CartButton
            data-test="button-view-cart"
            fullWidth
            loadOnRouteChange
            to="cart"
            outlined
          >
            <FormattedMessage defaultMessage="View Cart" id="5BTYa0" />
          </CartButton>
        </Link>
      </Footer>
      {authMode === AuthMode.Modern && (
        <CentralizedModal
          isOpen={isCentralizedModalOpen}
          onClose={() => setIsCentralizedModalOpen(false)}
          onComplete={() => setIsCentralizedModalOpen(false)}
          onAuthSuccess={() => {
            setIsCentralizedModalOpen(false);
            setSideCartState(false);
            const checkoutUrl = WuiltPayState?.enabled
              ? getPaymentLink(handle, cartId, token, localeCode)
              : `/checkout/${cartId}`;
            router.push(checkoutUrl);
          }}
        />
      )}
    </Container>
  );
};

export default SideCartFooter;

const Container = styled(FlexCol)`
  padding: 20px;
  box-shadow: 0px -4px 4px 0px rgba(226, 226, 226, 0.5);
  background-color: ${themeColor("white")};
`;

const Subtotal = styled(FlexRow)`
  background-color: rgba(0, 159, 41, 0.1);
  border-radius: 4px;
  padding: 16px;
`;

const Footer = styled(FlexRow)`
  gap: 16px;
`;

const CheckoutButton = styled(PrimaryButton)`
  padding-block: 12px;
  background-color: ${themeColor("primary")};
  color: ${themeColor("white")};
  border-color: ${themeColor("primary")};
`;

const CartButton = styled(PrimaryButton)`
  padding-block: 12px;
  border-color: ${themeColor("primary")};
  color: ${themeColor("primary")};
`;
